import { useEffect, useState } from "react";
import { Menu, X, MessageCircle } from "lucide-react";
import { NAV_LINKS, BRAND, whatsappUrl } from "../data/content";
import { ThemeToggle } from "./ThemeToggle";

export const Navbar = () => {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [active, setActive] = useState("inicio");

    useEffect(() => {
        const onScroll = () => {
            setScrolled(window.scrollY > 20);
            // active section detection
            for (const link of [...NAV_LINKS].reverse()) {
                const el = document.getElementById(link.id);
                if (el && el.getBoundingClientRect().top <= 120) {
                    setActive(link.id);
                    break;
                }
            }
        };
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (id) => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        setOpen(false);
    };

    // Cuando la barra está transparente (arriba del todo) siempre va sobre el
    // fondo oscuro del Hero, sin importar si el sitio está en modo día o
    // noche — por eso usamos colores claros fijos en ese estado en vez de
    // los tokens de tema (que en modo día son oscuros y quedan invisibles).
    const heroMode = !scrolled;

    return (
        <header
            data-testid="navbar"
            className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "bg-background/80 backdrop-blur-xl border-b border-gold/20"
                    : "bg-transparent"
            }`}
        >
            <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-10">
                {/* Logo */}
                <button
                    onClick={() => go("inicio")}
                    data-testid="nav-logo"
                    className="group flex items-center gap-3"
                >
                    <div
                        className={`relative flex h-11 w-11 items-center justify-center rounded-full border-2 bg-background shadow-[0_0_0_3px_hsl(var(--background))] ${
                            heroMode ? "border-gold-bright" : "border-gold"
                        }`}
                    >
                        <span className={`font-display text-xl ${heroMode ? "text-gold-bright" : "text-gold"}`}>M</span>
                        <span className={`absolute -inset-1 rounded-full border ${heroMode ? "border-gold-bright/30" : "border-gold/30"}`} />
                    </div>
                    <div className="hidden sm:block text-left leading-tight">
                        <div className={`font-display text-base tracking-wide ${heroMode ? "text-white" : "text-foreground"}`}>
                            Mariachi
                        </div>
                        <div className={`font-display text-sm tracking-[0.25em] ${heroMode ? "text-gold-bright" : "text-gold"}`}>
                            MEXICANÍSIMO
                        </div>
                    </div>
                </button>

                {/* Desktop links */}
                <nav className="hidden items-center gap-1 md:flex">
                    {NAV_LINKS.map((l) => (
                        <button
                            key={l.id}
                            onClick={() => go(l.id)}
                            data-testid={`nav-link-${l.id}`}
                            className={`relative px-4 py-2 text-sm font-medium tracking-wide transition-colors ${
                                active === l.id
                                    ? heroMode ? "text-gold-bright" : "text-gold"
                                    : heroMode ? "text-white/85 hover:text-white" : "text-foreground/70 hover:text-foreground"
                            }`}
                        >
                            {l.label}
                            {active === l.id && (
                                <span className={`absolute -bottom-0.5 left-1/2 h-[2px] w-6 -translate-x-1/2 ${heroMode ? "bg-gold-bright" : "bg-gold"}`} />
                            )}
                        </button>
                    ))}
                </nav>

                {/* Right actions */}
                <div className="flex items-center gap-2 md:gap-3">
                    <ThemeToggle />
                    <a
                        href={whatsappUrl()}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="nav-whatsapp-btn"
                        className="hidden items-center gap-2 rounded-full border border-gold bg-gold px-5 py-2.5 text-sm font-semibold text-[hsl(var(--primary-foreground))] transition hover:scale-[1.03] hover:shadow-[0_0_30px_hsl(var(--gold)/0.5)] md:inline-flex"
                    >
                        <MessageCircle className="h-4 w-4" />
                        Reservar
                    </a>
                    <button
                        className={`md:hidden inline-flex h-10 w-10 items-center justify-center rounded-full border ${
                            heroMode ? "border-gold-bright/40 text-gold-bright" : "border-gold/40 text-gold"
                        }`}
                        onClick={() => setOpen((o) => !o)}
                        aria-label="Abrir menú"
                        data-testid="mobile-menu-btn"
                    >
                        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>
            </div>

            {/* Mobile menu */}
            <div
                className={`md:hidden overflow-hidden bg-background/95 backdrop-blur-xl border-b border-gold/20 transition-[max-height] duration-500 ease-in-out ${
                    open ? "max-h-[560px]" : "max-h-0"
                }`}
            >
                <nav className="flex flex-col px-6 py-4">
                    {NAV_LINKS.map((l) => (
                        <button
                            key={l.id}
                            onClick={() => go(l.id)}
                            data-testid={`mobile-nav-link-${l.id}`}
                            className="border-b border-gold/10 py-3 text-left font-serif-pf text-lg text-foreground hover:text-gold"
                        >
                            {l.label}
                        </button>
                    ))}
                    <a
                        href={whatsappUrl()}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="mobile-whatsapp-btn"
                        className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-gold px-5 py-3 font-semibold text-[hsl(var(--primary-foreground))]"
                    >
                        <MessageCircle className="h-4 w-4" /> Reservar por WhatsApp
                    </a>
                    <p className="mt-3 text-center text-xs text-muted-foreground">
                        {BRAND.phoneDisplay} · {BRAND.city}
                    </p>
                </nav>
            </div>
        </header>
    );
};

export default Navbar;
